import * as React from 'react';
import TimeEntry from 'api/immutables/ImmutableTimeEntry';
import { DateTime } from 'luxon';
import * as Styled from './styled';
import { Add, ArrowDownward, ArrowUpward } from '@material-ui/icons';
import { MenuItem, Select, Tooltip, IconButton } from '@material-ui/core';
import { ValidationState } from 'api/immutables/validators';
import TimeEntryDropTarget from '../../containers/Home/TimeEntryDropTarget';
import { formatDuration, getDateFormat } from '../../util/date';
import { TKConsumer } from 'common/TKProvider';
import { ApprovalStatus, TimeKeeperAssignment, AggregateTotals, TimeEntrySortType } from 'api/types/types';
import { withTranslation } from 'react-i18next';
import { i18n } from 'i18next';

interface Props {
    entries: TimeEntry[];
    date: DateTime;
    onChange: (t: TimeEntry, newVState?: ValidationState, durVState?: boolean) => void;
    selected: number[];
    isMgmtDashboard?: boolean;
    onSelect: (ids: number[]) => void;
    expanded: number[];
    validationMap?: Map<number, ValidationState>;
    durValidationMap?: Map<number, boolean | undefined>;
    onExpand: (ids: number[]) => void;
    onCreateEntry?: (date: DateTime) => void;
    onPost: (t: TimeEntry) => void;
    onUnpost?: (t: TimeEntry) => void;
    onSave?: (t: TimeEntry) => void;
    onDelete?: (t: TimeEntry) => void;
    onCancel: (t: TimeEntry) => void;
    onCopy?: (t: TimeEntry) => void;
    onEdit: (t: TimeEntry) => void; 
    onSplit?: (t: TimeEntry) => void;
    onMerge?: (t: TimeEntry) => void;
    onTransfer?: (t: TimeEntry) => void;
    onApprove?: (t: TimeEntry) => void;
    onReject?: (t: TimeEntry) => void;
    aggregateTotals?: (date: DateTime[]) => AggregateTotals;
    onDrop?: () => void;
    buildCodeSets?: (id: number) => void;
    timeCastEnabledInDayView?: boolean;
    onSetFieldLoader?: (value: boolean) => void;
    showSortType?: boolean;
    // tslint:disable-next-line:no-any
    t: any;
    i18n: i18n;
    sortType?: TimeEntrySortType;
    onSortTypeChange?: (sortType: TimeEntrySortType) => void;
    isAscending?: boolean;
    onToggleSortDirection?: () => void;
}

class TimeEntryList extends React.Component<Props> {
    
    select = (id: number) => () => {
        let selected = this.props.selected.slice();
        if (selected.includes(id)) {
            selected = selected.filter((s) => s !== id);
        } else {
            selected.push(id);
        }
        this.props.onSelect(selected);
    }
    
    expand = (id: number) => () => {
        let expanded = this.props.expanded.slice();
        if (expanded.includes(id)) {
            expanded = expanded.filter((s) => s !== id);
        } else {
            expanded.push(id);
        }
        this.props.onExpand(expanded);
    }
    
    selectAll = () => {
        let entries = this.props.entries.filter((e) =>
            !e.isPosted() || (this.props.isMgmtDashboard && e.isPosted() && e.approvalStatus === ApprovalStatus.UNREVIEWED)
        );
        let rest = this.props.selected.filter(s => entries.every((e) => e.id !== s));
        if (entries.every((e) => this.props.selected.includes(e.id as number))) {
            this.props.onSelect(rest);
        } else {
            this.props.onSelect(entries.map(e => e.id as number).concat(rest));
        }
    }
    
    onCreateEntry = () => {
        this.props.onCreateEntry!(this.props.date);
    }
    
    render() {
        const {
            date,
            entries,
            expanded, 
            selected,
            onChange,
            onPost,
            onUnpost,
            onSave,
            onDelete,
            onCancel,
            onCopy,
            onEdit,
            onSplit,
            onMerge,
            onTransfer,
            onApprove,
            onReject,
            durValidationMap,
            validationMap,
            onDrop,
            buildCodeSets,
            aggregateTotals,
            timeCastEnabledInDayView,
            onSetFieldLoader,
            isMgmtDashboard,
            showSortType,
            sortType,
            onSortTypeChange,
            isAscending,
            onToggleSortDirection
        } = this.props;
        let allSelected = entries.every((e) => selected.includes(e.id as number));
        const totals = aggregateTotals! ? aggregateTotals!([date]) : undefined;
        let moreThanOneSelected = selected.length > 1;
        return (
            <>
                <TKConsumer>
                    { (tk: TimeKeeperAssignment) => 
                <Styled.TimeEntryListHeader>
                    {/* <Checkbox
                        checked={allSelected}
                        indeterminate={anySelected && !allSelected}
                        onClick={(e) => e.stopPropagation()}
                        onChange={this.selectAll}
                        disableRipple={true}
                        style={{padding: 0}}
                    /> */}
                    <Styled.ListDate 
                        selected={allSelected}
                        writable={!tk.writable}
                        onClick={this.selectAll}
                        tabIndex={0}
                        aria-label={date.toFormat('EEEE') + ', ' + date.toFormat(getDateFormat())}
                        onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              this.selectAll();
                            }
                        }}
                    >
                        {date.toFormat('EEE') + ', ' + date.toFormat(getDateFormat())}
                    </Styled.ListDate>
                    {totals &&
                        <>
                            {!isMgmtDashboard &&
                                <>
                                    <Styled.Posted justifyContent="flex-end">
                                        {this.props.t('list.row.info.posted') + ': ' + formatDuration(totals.posted)}
                                    </Styled.Posted>
                                    <Styled.Draft justifyContent="center">
                                        {this.props.t('list.row.info.draft') + ': ' + formatDuration(totals.draft)}
                                    </Styled.Draft>
                                </>
                            }
                            <Styled.TotalDuration justifyContent="left">
                                {this.props.t('list.row.info.total') + ': ' + formatDuration(totals.posted + totals.draft)}
                            </Styled.TotalDuration>
                        </>
                    }
                    {showSortType &&
                        <Styled.SortEntries
                            aria-label={this.props.t('header.sort_by.tooltip')}
                            title={this.props.t('header.sort_by.tooltip')}
                            style={{ display: 'flex', alignItems: 'center' }}
                        >
                            <Select
                                value={sortType}
                                onChange={(event) => onSortTypeChange!(event.target.value as TimeEntrySortType)}
                                disableUnderline={true}
                            >
                                {(Object.keys(TimeEntrySortType) as TimeEntrySortType[]).map((val) => (
                                    <MenuItem value={val} key={val}>
                                        {this.props.t(`header.sort_by.options.${val.toLowerCase()}`)}
                                    </MenuItem>
                                ))}
                            </Select>
                            {onToggleSortDirection && (
                                <Tooltip title={this.props.t(`header.sort_by.icon.${isAscending ? 'ascending' : 'descending'}.tooltip`)}>
                                    <IconButton
                                        onClick={onToggleSortDirection}
                                        style={{ marginLeft: 4, padding: 8 }}
                                    >
                                        {isAscending ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />}
                                    </IconButton>
                                </Tooltip>
                            )}
                        </Styled.SortEntries>
                    }
                    {tk.writable && !isMgmtDashboard &&
                            <Tooltip title={this.props.t('list.row.info.action.add.tooltip')}>
                                <Styled.StyledIconButton onClick={this.onCreateEntry}>
                                    <Add />
                                </Styled.StyledIconButton>
                            </Tooltip>
                    }
                </Styled.TimeEntryListHeader>}
                </TKConsumer>
                {entries.map((t) =>
                    <Styled.TimeEntryItem key={t.id as number}>
                        <TimeEntryDropTarget
                            validationState={validationMap ? validationMap.get(t.id!) : undefined}
                            durValidationState={durValidationMap ? durValidationMap.get(t.id!) : undefined}
                            timeEntry={t}
                            onChange={onChange}
                            onExpand={this.expand(t.id as number)}
                            expanded={expanded.includes(t.id as number)}
                            onSelect={this.select(t.id as number)}
                            selected={selected.includes(t.id as number)}
                            onPost={onPost}
                            onUnpost={onUnpost}
                            onSave={onSave}
                            onDelete={onDelete}
                            onCancel={onCancel}
                            onCopy={onCopy}
                            onEdit={onEdit}
                            onSplit={onSplit}
                            onMerge={onMerge}
                            isMergeable={moreThanOneSelected}
                            onTransfer={onTransfer}
                            onApprove={onApprove}
                            onReject={onReject}
                            isTransferable={moreThanOneSelected}
                            onDrop={onDrop}
                            buildCodeSets={buildCodeSets}
                            timeCastEnabledInDayView={timeCastEnabledInDayView}
                            onSetFieldLoader={onSetFieldLoader}
                            isMgmtDashboard={isMgmtDashboard}
                        />
                    </Styled.TimeEntryItem>
                )}
            </>
        );
    }
}

export default withTranslation(['timeentries'])(TimeEntryList);